import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { MatIconModule } from '@angular/material/icon';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { ActivatedRoute } from '@angular/router';

export interface ChapterMeta {
    folderId: number;
    title: string;
}

@Component({
    selector: 'app-user-guide',
    templateUrl: './user-guide.component.html',
    styleUrls: ['./user-guide.component.css'],
    imports: [CommonModule, MatIconModule, SpinnerComponent],
})
export class UserGuideComponent implements OnInit {
    chapters: ChapterMeta[] = [];
    activeChapter: ChapterMeta | null = null;
    renderedContent: SafeHtml = '';
    isLoading = false;

    constructor(private http: HttpClient, private sanitizer: DomSanitizer, private route: ActivatedRoute) { }

    ngOnInit(): void {
        this.http
            .get<ChapterMeta[]>('assets/user-guide/chapters.json')
            .subscribe((chapters) => {
                this.chapters = chapters;
                if (this.chapters.length > 0) {
                    const requestedChapter = Number(this.route.snapshot.queryParamMap.get('chapter'));
                    const chapter = this.chapters.find((item) => item.folderId === requestedChapter) ?? this.chapters[0];
                    this.loadChapter(chapter);
                }
            });
    }

    onContentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const anchor = target.closest('a');
        if (!anchor) return;

        const href = anchor.getAttribute('href') ?? '';
        const match = href.match(/^chapter:(\d+)$/);
        if (!match) return;

        event.preventDefault();
        const folderId = Number(match[1]);
        const chapter = this.chapters.find((c) => c.folderId === folderId);
        if (chapter) this.loadChapter(chapter);
    }

    loadChapter(chapter: ChapterMeta): void {
        if (this.activeChapter === chapter) return;
        this.activeChapter = chapter;
        this.isLoading = true;

        const mdPath = `assets/user-guide/${chapter.folderId}/content.md`;
        this.http.get(mdPath, { responseType: 'text' }).subscribe({
            next: (mdText) => {
                const html = marked.parse(mdText) as string;
                DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
                    if (data.attrName === 'href' && /^chapter:\d+$/.test(data.attrValue)) {
                        data.forceKeepAttr = true;
                    }
                });
                const clean = DOMPurify.sanitize(html, { ADD_ATTR: ['width', 'height'] });
                DOMPurify.removeHook('uponSanitizeAttribute');
                this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(clean);
                this.isLoading = false;
            },
            error: () => {
                this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(
                    '<p class="text-danger">Could not load chapter content.</p>'
                );
                this.isLoading = false;
            },
        });
    }
}

